"use client";
import React, { useState, useEffect } from "react";
import {
  Eye,
  Undo2,
  Redo2,
  Monitor,
  Tablet,
  Smartphone,
  Upload,
  Sun,
  Moon,
  Save,
  Keyboard,
  CheckCircle,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { useIsCompactView } from "@/hooks/useIsCompactView";
import { useUndoRedo } from "@/hooks/useUndoRedo";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { setShowFullscreenEditor } from "@/store/slices/ui.slice";
import { selectSectionsArray } from "@/store/selectors/webbuilder.selectors";
import {
  submitPageRequest,
  createDraftRequest,
  draftFormReset,
} from "@/store/slices/page.slice";
import { store } from "@/store/store";
import { Page } from "@/types/models/page";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { PageSelectDropdown } from "@/components/webbuilder/ui/elements/PageSelectDropdown";

interface TopBarProps {
  isDark: boolean;
  onToggleTheme: () => void;
  currentDevice: "desktop" | "tablet" | "mobile";
  onDeviceChange: (device: "desktop" | "tablet" | "mobile") => void;
  showDeviceSelector?: boolean;
  onTriggerCanvasShimmer?: () => void;
  pages?: Page[];
  isLoadingPages?: boolean;
  selectedPageId?: string;
  currentPage?: Page | null;
  sections?: any[];
  onPageChange?: (pageId: string) => void;
  onOpenAddPage?: () => void;
  onPreview?: () => void;
  onSave?: () => void;
  onPublish?: () => void;
  onPublishAll?: () => void;
  onShowKeyboardShortcuts?: () => void;
  isPagePublished?: boolean;
  hasUnpublishedChanges?: boolean;
}

export function TopBar({
  isDark,
  onToggleTheme,
  currentDevice,
  onDeviceChange,
  showDeviceSelector = true,
  onTriggerCanvasShimmer,
  pages = [],
  isLoadingPages = false,
  selectedPageId,
  currentPage: currentPageProp,
  sections: sectionsProp,
  onPageChange,
  onOpenAddPage,
  onPreview,
  onSave,
  onPublish,
  onPublishAll,
  onShowKeyboardShortcuts,
  isPagePublished = false,
  hasUnpublishedChanges = false,
}: TopBarProps) {
  const [isShining, setIsShining] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const isCompact = useIsCompactView();
  const { undo, redo, canUndo, canRedo, getHistoryInfo } = useUndoRedo();
  const _dispatch = useAppDispatch();
  const showFullscreen = useAppSelector(
    (s) => (s as any).ui?.showFullscreenEditor,
  );

  // Safely read the page slice to avoid runtime errors when a different Redux provider/store is active
  const pageState = useAppSelector(
    (state) => (state && (state as any).page) || null,
  );
  // Use prop if provided, otherwise fall back to Redux state
  const currentPageFromRedux = pageState?.currentPage || null;
  const currentPage =
    currentPageProp !== undefined ? currentPageProp : currentPageFromRedux;
  const sectionsFromRedux =
    (useAppSelector(selectSectionsArray) as any[]) || [];
  // Use sections prop if provided, otherwise fall back to Redux state
  const sections =
    sectionsProp !== undefined ? sectionsProp : sectionsFromRedux;
  const _formState = pageState?.form || {
    loading: false,
    success: false,
    error: null,
  };
  const _draftFormState = pageState?.draftForm || {
    loading: false,
    success: false,
    error: null,
  };

  // Get current store info from auth state
  const currentStoreInfo = useAppSelector(
    (state) => (state && (state as any).auth?.currentStoreInfo) || null,
  );

  const handlePublish = () => {
    if (onPublish) {
      // Use external handler if provided
      onPublish();
      return;
    }

    // Default publish behavior
    if (!selectedPageId || !currentPage) {
      toast.error("No page selected for publishing");
      return;
    }

    if (sections.length === 0) {
      toast.error("No sections to publish");
      return;
    }

    setIsPublishing(true);
    setIsShining(true);

    // Trigger canvas shimmer effect
    onTriggerCanvasShimmer?.();

    // Send only the fields that need to be updated
    const updatePayload = {
      sections: sections.map((section) => ({
        ...section,
        // Ensure the section has the correct page_id
        page_id: selectedPageId,
        // Update the updated_at timestamp
        updated_at: new Date().toISOString(),
      })),
      // Set status to published
      status: "published",
    };

    // Dispatch directly to the store (bypassing React context due to nested providers)
    store.dispatch(
      submitPageRequest({
        data: updatePayload as Page,
        pageId: selectedPageId,
      }),
    );
  };

  const handleSaveAsDraft = () => {
    if (onSave) {
      // Use external handler if provided
      onSave();
      return;
    }

    // Default save behavior
    if (!selectedPageId || !currentPage) {
      toast.error("No page selected for saving");
      return;
    }

    if (sections.length === 0) {
      toast.error("No sections to save");
      return;
    }

    setIsSavingDraft(true);

    // Create a new draft using the draft API - dispatch directly to store
    store.dispatch(createDraftRequest({ pageId: selectedPageId }));
  };

  const handlePreview = () => {
    if (!selectedPageId || !currentPage) {
      toast.error("No page selected for preview");
      return;
    }

    if (onPreview) {
      onPreview();
    } else {
      // Use the root domain from current store info
      if (!currentStoreInfo?.domain) {
        toast.error("No domain configured for this store.");
        return;
      }

      // Construct the preview URL using the store's root domain and page slug
      const pageSlug = currentPage.slug || "";
      const domainUrl = currentStoreInfo.domain.startsWith("http")
        ? currentStoreInfo.domain
        : `https://${currentStoreInfo.domain}`;
      const previewUrl = `${domainUrl}/${pageSlug}`;

      window.open(previewUrl, "_blank");
    }
  };

  // Handle form state changes for publishing feedback - subscribe directly to store
  useEffect(() => {
    if (!isPublishing) return;

    const unsubscribe = store.subscribe(() => {
      const state = store.getState();
      const pageState = (state as any).page;
      const form = pageState?.form || {
        loading: false,
        success: false,
        error: null,
      };

      if (form.success) {
        toast.success("Page published successfully!");
        setIsPublishing(false);
        setIsShining(false);
        unsubscribe();
      } else if (form.error) {
        toast.error("Failed to publish page. Please try again.");
        console.error("❌ Failed to publish page:", form.error);
        setIsPublishing(false);
        setIsShining(false);
        unsubscribe();
      }
    });

    return () => unsubscribe();
  }, [isPublishing]);

  // Handle draft form state changes - subscribe directly to store
  useEffect(() => {
    if (!isSavingDraft) return;

    const unsubscribe = store.subscribe(() => {
      const state = store.getState();
      const pageState = (state as any).page;
      const draftForm = pageState?.draftForm || {
        loading: false,
        success: false,
        error: null,
      };

      if (draftForm.success) {
        toast.success("Draft saved successfully!");
        setIsSavingDraft(false);
        // Reset the draft form state
        store.dispatch(draftFormReset());
        unsubscribe();
      } else if (draftForm.error) {
        toast.error("Failed to save draft. Please try again.");
        console.error("❌ Failed to save draft:", draftForm.error);
        setIsSavingDraft(false);
        // Reset the draft form state
        store.dispatch(draftFormReset());
        unsubscribe();
      }
    });

    return () => unsubscribe();
  }, [isSavingDraft]);

  return (
    <div
      className="p-4 animate-fadeIn relative z-50"
      style={{ animationDelay: "0.2s" }}
    >
      <div
        className="bg-white dark:bg-auth-card rounded-full shadow-theme-lg border border-border dark:border-auth-background px-6 py-3 flex items-center gap-4 animate-slideDown"
        style={{ animationDelay: "0.1s" }}
      >
        {/* Left Section - Site Info */}
        <div
          className="flex items-center space-x-3 animate-popIn"
          style={{ animationDelay: "0.1s" }}
        >
          <div className="relative w-8 h-8 rounded-full shadow-xl bg-white overflow-hidden border border-gray-200">
            <img
              src="/assets/logo.png"
              alt="Site logo"
              width={32}
              height={32}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
              MS
            </div>
          </div>
          <div
            className={`flex flex-col transition-all duration-300 overflow-hidden ${isCompact ? "w-0 opacity-0" : "opacity-100"}`}
          >
            <span className="text-sm font-medium whitespace-nowrap">
              {currentStoreInfo?.storeName || "My Store"}
            </span>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {currentStoreInfo?.domain || "mystore.com"}
            </span>
          </div>
        </div>

        {/* Page Selector */}
        <div className="w-[260px] shrink-0">
          <PageSelectDropdown
            pages={pages}
            selectedPageId={selectedPageId}
            onPageChange={onPageChange}
            isLoading={isLoadingPages}
            onOpenAddPage={onOpenAddPage}
          />
        </div>

        {/* Actions Container */}
        <div className="flex items-center space-x-4 ml-auto">
          {/* Preview Button */}
          <ButtonOutline
            onClick={handlePreview}
            className={`flex items-center ${!isCompact ? "space-x-2" : ""} dark:bg-auth-background dark:border-auth-background hover:bg-primary hover:text-primary-foreground h-10 ${isCompact ? "w-10 p-0 justify-center" : "px-3"} text-sm rounded-full transition-all duration-300 animate-popIn topbar-compact-button`}
            style={{ animationDelay: "0.3s" }}
          >
            <Eye className="w-4 h-4" />
            {!isCompact && <span>Preview</span>}
          </ButtonOutline>

          {/* Theme Switcher */}
          <ButtonOutline
            onClick={onToggleTheme}
            className="dark:bg-auth-background dark:border-auth-background hover:bg-primary hover:text-primary-foreground h-10 w-10 p-0 flex items-center justify-center transition-colors animate-popIn"
            style={{ animationDelay: "0.4s" }}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDark ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
          </ButtonOutline>

          {/* Keyboard Shortcuts Help */}
          {onShowKeyboardShortcuts && (
            <ButtonOutline
              onClick={onShowKeyboardShortcuts}
              className="dark:bg-auth-background dark:border-auth-background hover:bg-primary hover:text-primary-foreground h-10 w-10 p-0 flex items-center justify-center transition-colors animate-popIn"
              style={{ animationDelay: "0.45s" }}
              aria-label="Show keyboard shortcuts"
            >
              <Keyboard className="w-4 h-4" />
            </ButtonOutline>
          )}

          {/* Undo/Redo */}
          <TooltipProvider>
            <div
              className="flex items-center space-x-2 animate-popIn"
              style={{ animationDelay: "0.5s" }}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <ButtonOutline
                    onClick={undo}
                    disabled={!canUndo}
                    className={`dark:bg-auth-background dark:border-auth-background hover:bg-primary hover:text-primary-foreground h-10 ${isCompact ? "w-10 p-0 justify-center" : "px-3"} flex items-center ${!isCompact ? "space-x-2" : ""} text-sm transition-all duration-300 topbar-compact-button ${!canUndo ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <Undo2 className="w-4 h-4" />
                    {!isCompact && <span>Undo</span>}
                  </ButtonOutline>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Undo last action{" "}
                    {canUndo
                      ? `(${getHistoryInfo().currentIndex} of ${getHistoryInfo().totalStates})`
                      : "(No actions to undo)"}
                  </p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <ButtonOutline
                    onClick={redo}
                    disabled={!canRedo}
                    className={`dark:bg-auth-background dark:border-auth-background hover:bg-primary hover:text-primary-foreground h-10 ${isCompact ? "w-10 p-0 justify-center" : "px-3"} flex items-center ${!isCompact ? "space-x-2" : ""} text-sm transition-all duration-300 topbar-compact-button ${!canRedo ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <Redo2 className="w-4 h-4" />
                    {!isCompact && <span>Redo</span>}
                  </ButtonOutline>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Redo next action{" "}
                    {canRedo
                      ? `(${getHistoryInfo().totalStates - getHistoryInfo().currentIndex - 1} actions available)`
                      : "(No actions to redo)"}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>

          {showDeviceSelector && (
            <div
              className="flex items-center bg-muted dark:bg-auth-background rounded-lg p-1 h-10 animate-popIn"
              style={{ animationDelay: "0.6s" }}
            >
              <button
                onClick={() => onDeviceChange("desktop")}
                className={`h-8 w-8 rounded-md transition-colors flex items-center justify-center ${
                  currentDevice === "desktop"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-background dark:hover:bg-auth-card dark:hover:text-primary-foreground"
                }`}
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDeviceChange("tablet")}
                className={`h-8 w-8 rounded-md transition-colors flex items-center justify-center ${
                  currentDevice === "tablet"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-background dark:hover:bg-auth-card dark:hover:text-primary-foreground"
                }`}
              >
                <Tablet className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDeviceChange("mobile")}
                className={`h-8 w-8 rounded-md transition-colors flex items-center justify-center ${
                  currentDevice === "mobile"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-background dark:hover:bg-auth-card dark:hover:text-primary-foreground"
                }`}
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Fullscreen toggle */}
          <ButtonOutline
            onClick={() => {
              const nextFullscreen = !showFullscreen;
              _dispatch(setShowFullscreenEditor(nextFullscreen));
            }}
            className="dark:bg-auth-background dark:border-auth-background hover:bg-primary hover:text-primary-foreground h-10 w-10 p-0 flex items-center justify-center transition-colors animate-popIn"
            style={{ animationDelay: "0.65s" }}
            aria-label={
              showFullscreen
                ? "Exit fullscreen editor"
                : "Open fullscreen editor"
            }
          >
            {showFullscreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </ButtonOutline>

          {/* Separator */}
          <div
            className="h-6 w-px bg-border dark:bg-auth-background animate-popIn"
            style={{ animationDelay: "0.7s" }}
          ></div>

          {/* Publish Button */}
          <div className="flex items-center gap-1">
            <Button
              variant={
                isPagePublished && !hasUnpublishedChanges
                  ? "outline"
                  : "default"
              }
              size="pill-lg"
              onClick={handlePublish}
              disabled={
                isPublishing ||
                !selectedPageId ||
                !currentPage ||
                (isPagePublished && !hasUnpublishedChanges)
              }
              className={`${!isCompact ? "space-x-1.5" : ""} ${isCompact ? "w-10 p-0 justify-center" : "px-3"} text-sm animate-popIn topbar-compact-button transition-all duration-300 ${
                isPagePublished && !hasUnpublishedChanges
                  ? "dark:bg-auth-background dark:border-auth-background opacity-75 cursor-not-allowed"
                  : `text-white shine-button ${isShining ? "shine-triggered" : ""}`
              } ${isPublishing ? "opacity-75 cursor-not-allowed" : ""}`}
              style={{ animationDelay: "0.9s" }}
              leftIcon={
                isPagePublished && !hasUnpublishedChanges ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <Upload
                    className={`w-4 h-4 ${isPublishing ? "animate-spin" : ""}`}
                  />
                )
              }
            >
              {!isCompact && (
                <span>
                  {isPublishing
                    ? "Publishing..."
                    : isPagePublished && !hasUnpublishedChanges
                      ? "Published"
                      : "Publish"}
                </span>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  size="pill-lg"
                  className="px-2 text-white"
                  disabled={isPublishing || pages.length === 0}
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handleSaveAsDraft}
                  disabled={isSavingDraft || !selectedPageId || !currentPage}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save as draft
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onPublishAll?.()}>
                  Publish all pages
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
}
